import { Button } from './button';
import { Input } from './input';
import { ModalCenter, ModalCenterRef } from './ModalCenter';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { teamFieldsToUtc, utcToTeamFields } from '@shared/utils/deadlineTimezone';

export interface EditDeadlineModalRef {
  /**
   * @param currentDeadlineUtc límite vigente en UTC `Y-m-d H:i:s`
   * @param onConfirm recibe la nueva fecha ya convertida a UTC
   */
  open: (currentDeadlineUtc: string | null, onConfirm: (deadlineUtc: string) => Promise<void>) => void;
}

interface EditDeadlineModalProps {
  timeZone: string;
}

export const EditDeadlineModal = forwardRef<EditDeadlineModalRef, EditDeadlineModalProps>(({ timeZone }, ref) => {
  const { t } = useTranslation('ui');
  const modalRef = useRef<ModalCenterRef>(null);
  const confirmCallbackRef = useRef<((deadlineUtc: string) => Promise<void>) | null>(null);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useImperativeHandle(ref, () => ({
    open: (currentDeadlineUtc, onConfirm) => {
      const fields = currentDeadlineUtc ? utcToTeamFields(currentDeadlineUtc, timeZone) : null;
      setDate(fields?.date ?? '');
      setTime(fields?.time ?? '');
      setError(null);
      setIsProcessing(false);
      confirmCallbackRef.current = onConfirm;
      modalRef.current?.openModal();
    },
  }));

  const handleClose = () => {
    if (isProcessing) return;
    modalRef.current?.closeModal();
  };

  const handleConfirm = async () => {
    const deadlineUtc = teamFieldsToUtc(date, time, timeZone);
    if (!deadlineUtc) {
      setError(t('deadline_modal.invalid', 'Introduce una fecha y una hora válidas'));
      return;
    }

    if (!confirmCallbackRef.current) return;

    setError(null);
    setIsProcessing(true);
    try {
      await confirmCallbackRef.current(deadlineUtc);
      modalRef.current?.closeModal();
    } catch (err: unknown) {
      // El modal permanece abierto para que el usuario pueda reintentar.
      const response = (err as { response?: { data?: { error?: { message?: string }; message?: string } } })?.response;
      setError(
        response?.data?.error?.message ??
          response?.data?.message ??
          t('deadline_modal.error', 'No se pudo actualizar la fecha límite'),
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={t('deadline_modal.title', 'Editar fecha límite')}
      type="default"
      width="default"
      data-testid="edit-deadline-modal"
      onCloseModal={() => {
        setIsProcessing(false);
        setError(null);
      }}
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <label className="text-sm font-medium text-text" htmlFor="edit-deadline-date">
              {t('deadline_modal.date', 'Fecha')}
            </label>
            <Input
              id="edit-deadline-date"
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              disabled={isProcessing}
              data-testid="edit-deadline-date-input"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-text" htmlFor="edit-deadline-time">
              {t('deadline_modal.time', 'Hora')}
            </label>
            <Input
              id="edit-deadline-time"
              type="time"
              value={time}
              onChange={(event) => setTime(event.target.value)}
              disabled={isProcessing}
              data-testid="edit-deadline-time-input"
            />
          </div>
        </div>

        <p className="text-xs text-complementary-text" data-testid="edit-deadline-help">
          {t('deadline_modal.help', 'Puedes ajustar la fecha para contemplar festivos o necesidades operativas')}
        </p>

        {error && (
          <p className="text-xs text-error" role="alert" data-testid="edit-deadline-error">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-card-border">
          <Button
            variant="outline"
            size="lg"
            onClick={handleClose}
            disabled={isProcessing}
            data-testid="edit-deadline-cancel-button"
          >
            {t('deadline_modal.cancel', 'Cancelar')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleConfirm}
            disabled={isProcessing}
            data-testid="edit-deadline-save-button"
          >
            {isProcessing ? t('deadline_modal.saving', 'Guardando...') : t('deadline_modal.save', 'Guardar cambios')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

EditDeadlineModal.displayName = 'EditDeadlineModal';
